import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStore } from '@tanstack/react-form';
import type { ReactNode } from 'react';
import { useState } from 'react';

import {
  type ImpactScoreMetricType,
  type ImpactScoreWeight,
  type ImpactScoreWeightSettings,
  mutateImpactScoreWeightsSetAll,
  type useImpactScoreWeights,
} from '@/api/settings';
import { useAppForm } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import { Panel } from '@/components/panels';
import Prompt from '@/components/Prompt';
import { SupportLink } from '@/components/typography';
import { cn } from '@/lib/utils';
import { useNotification } from '@/providers/Notification';

import CompanyImpactScoreMetricTypeSection, {
  type ResolvedImpactScoreWeight,
} from './CompanyImpactScoreMetricTypeSection';
import CompanySettingsActionBar from './CompanySettingsActionBar';

type Props = {
  settings: ImpactScoreWeightSettings;
  mutate: ReturnType<typeof useImpactScoreWeights>['mutate'];
  canEdit: boolean;
  startContent?: ReactNode;
  onDirtyChange?: (isDirty: boolean) => void;
  onEditModeChange?: (isEditMode: boolean) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

const metricTypeOrder: ImpactScoreMetricType[] = [
  'digital_metrics',
  'pipeline_metrics',
  'offline_campaign_metrics',
  'sales_activity_metrics',
];

const resolveImpactScoreWeights = (weights: ImpactScoreWeight[]): ResolvedImpactScoreWeight[] =>
  weights.map((weight, index) => ({
    ...weight,
    fieldName: `weights[${index}].weight`,
  }));

const groupWeightsByMetricType = (weights: ImpactScoreWeight[]) => {
  const resolvedWeights = resolveImpactScoreWeights(weights);

  return metricTypeOrder
    .map((metricType) => ({
      metricType,
      weights: resolvedWeights.filter((weight) => weight.metricType === metricType),
    }))
    .filter((group) => group.weights.length > 0);
};

const CompanyImpactScoringSettingsForm = ({
  settings,
  mutate,
  canEdit,
  startContent,
  onDirtyChange,
  onEditModeChange,
  onSubmittingChange,
}: Props) => {
  const { pushNotification, removeNotification } = useNotification();
  const [isEditMode, setIsEditMode] = useState(false);

  const form = useAppForm({
    defaultValues: settings,
    listeners: {
      onChange: ({ formApi }) => {
        onDirtyChange?.(formApi.state.isDirty);
      },
    },
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        removeNotification({ type: 'error' });
        onSubmittingChange?.(true);

        try {
          const success = await mutateImpactScoreWeightsSetAll({
            customScoringModelEnabled: value.customScoringModelEnabled,
            weights: value.weights.map(({ id, weight }) => ({ id, weight })),
          });

          if (!success) {
            throw new Error();
          }

          mutate(
            {
              impactScoreWeights: {
                get: value,
              },
            },
            { revalidate: false },
          );
          pushNotification({
            type: 'success',
            message: t`Success! Impact scoring settings have been saved.`,
          });
          setIsEditMode(false);
          onEditModeChange?.(false);
          formApi.reset(value, { keepDefaultValues: true });
          onDirtyChange?.(false);
        } catch {
          pushNotification({
            type: 'error',
            message: t`Sorry, we were unable to update impact scoring settings due to an error. Please review your changes and retry.`,
          });
        } finally {
          onSubmittingChange?.(false);
        }
      },
    },
  });

  const { isDirty, isSubmitting } = useStore(form.store, (state) => ({
    isDirty: state.isDirty,
    isSubmitting: state.isSubmitting,
  }));

  const handleEditModeChange = (desiresEditMode: boolean) => {
    setIsEditMode(desiresEditMode);
    onEditModeChange?.(desiresEditMode);

    if (desiresEditMode) {
      removeNotification({ type: 'success' });
    }
  };

  const handleCancel = () => {
    form.reset(settings, { keepDefaultValues: true });
    setIsEditMode(false);
    onEditModeChange?.(false);
    onDirtyChange?.(false);
  };

  const handleSave = async () => {
    await form.handleSubmit();
  };

  return (
    <div className="flex flex-col gap-6">
      <Prompt
        message={t`Are you sure you want to exit without saving your impact scoring settings?`}
        shouldPreventNavigation={isEditMode && isDirty && !isSubmitting}
      />
      <CompanySettingsActionBar
        isEditMode={isEditMode}
        isSaveEnabled={isDirty}
        isSaving={isSubmitting}
        startContent={startContent}
        showActions={canEdit}
        onEditModeChange={handleEditModeChange}
        onCancel={handleCancel}
        onSave={handleSave}
      />

      <Panel
        title={t`Impact Scoring (Weighted)`}
        infoPopover={{
          title: t`Impact Scoring`,
          body: (
            <>
              <Trans>
                Define how engagement signals contribute to a single account-level Impact Score by
                assigning weights to each metric. The score is additive: each metric&apos;s value in
                the selected timeframe is multiplied by its weight and summed into one number per
                company. Use weights from 0.00–100.00; set a weight to 0 to exclude a metric.
              </Trans>
              <span className="mt-2 block">
                <Trans>
                  When Custom Scoring Model is off, Impact Scores are not shown in the product. This
                  score is used across the platform for account prioritization, filtering, audience
                  building, and analysis in activity, vendor, campaign, and keyword views.{' '}
                  <SupportLink variant="body2" color="static-green" path="IMPACT_SCORE_SETUP">
                    Learn more
                  </SupportLink>
                </Trans>
              </span>
            </>
          ),
        }}
      >
        <form.AppForm>
          <form.Subscribe selector={(state) => state.values}>
            {(values) => {
              const groupedWeights = groupWeightsByMetricType(values.weights);
              const disabled = !isEditMode || !values.customScoringModelEnabled;

              return (
                <div className="flex flex-col gap-8">
                  <div>
                    <div className="flex items-center gap-3">
                      <FieldLabel>
                        <Trans>Custom Scoring Model</Trans>
                      </FieldLabel>
                      <form.AppField name="customScoringModelEnabled">
                        {(field) => (
                          <field.Switch
                            aria-label={t`Custom Scoring Model`}
                            disabled={!isEditMode}
                          />
                        )}
                      </form.AppField>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                      <Trans>
                        Assign weights to engagement metrics to calculate a single impact score for
                        each company
                      </Trans>
                    </p>
                  </div>

                  <div
                    className={cn(
                      'grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-2',
                      !values.customScoringModelEnabled && 'opacity-50',
                    )}
                  >
                    {groupedWeights.map(({ metricType, weights }) => (
                      <CompanyImpactScoreMetricTypeSection
                        key={metricType}
                        metricType={metricType}
                        weights={weights}
                        disabled={disabled}
                      />
                    ))}
                  </div>
                </div>
              );
            }}
          </form.Subscribe>
        </form.AppForm>
      </Panel>
    </div>
  );
};

export default CompanyImpactScoringSettingsForm;
